import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  getUserInfo,
  setAccessToken,
  loginAudit,
  selectAccessToken,
  clearSecureState
} from '../SecureRoute/slice';

/**
 * Component that initializes authentication token on app load
 * This ensures the token is available in Redux state even after page refresh
 */
const AuthInitializer: React.FC = () => {
  const { isLoading, isAuthenticated, getAccessTokenSilently, error } = useAuth0();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);

  useEffect(() => {
    // Wait for Auth0 to finish loading
    if (isLoading) {
      return;
    }

    // If there's an Auth0 error, clear the secure state
    if (error !== null && error !== undefined) {
      console.error('Auth0 error:', error);
      dispatch(clearSecureState());
      return;
    }

    // If user is authenticated but we don't have a token, fetch it
    if (isAuthenticated && (accessToken == null || accessToken === '')) {
      // eslint-disable-next-line
      getAccessTokenSilently()
        .then((token) => {
          dispatch(setAccessToken(token));
          // eslint-disable-next-line
          dispatch(getUserInfo());
          // eslint-disable-next-line
          dispatch(loginAudit());
        })
        .catch((error) => {
          console.error('Failed to get access token:', error);
          // Clear secure state on error
          dispatch(clearSecureState());
        });
    } else if (!isAuthenticated && accessToken !== null && accessToken !== undefined && accessToken !== '') {
      // If user is not authenticated but we have a token, clear secure state
      dispatch(clearSecureState());
    } else if (isAuthenticated && accessToken !== null && accessToken !== undefined && accessToken !== '') {
      // If we have both authentication and token, ensure user info is loaded
      // Only fetch if we don't have user info yet (to avoid unnecessary calls)
      // This will be handled by components that need user info
    }
  }, [isLoading, isAuthenticated, accessToken, getAccessTokenSilently, dispatch, error]);

  // This component doesn't render anything
  return null;
};

export default AuthInitializer;
